var debounce = window.debounce;

/**
 *  @class
 *  @function ThemeHeader
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
      this.ticking = false; // scroll optimization flag
    }

    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');

      // Use native CSS sticky for the header. The previous JS-based transform
      // approach was buggy on scroll-up across all browsers — we now hand
      // positioning to the browser, which handles it on the compositor with
      // zero JS involvement.
      this.injectStickyStyles();

      // Clear any leftover transform that previous versions of this script
      // may have applied. Positioning is no longer done via transform.
      this.style.transform = '';

      // ESC key close
      document.addEventListener('keyup', (e) => {
        if (e.code && e.code.toUpperCase() === 'ESCAPE') {
          if (this.toggle) {
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
          }
        }
      });

      // Mobile toggle
      const mobileToggle = this.toggle && this.toggle.querySelector('.mobile-toggle');
      if (mobileToggle) mobileToggle.addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      });

      // ------------------------------
      // Optimized Scroll Listener
      // ------------------------------
      // Positioning is handled by CSS sticky. This handler only updates
      // classes and CSS variables that other parts of the theme rely on.
      const onScroll = () => {
        if (!this.ticking) {
          window.requestAnimationFrame(() => {
            this.setStickyClass();
            this.setHeaderOffset();
            this.setHeaderHeight();
            if (document.querySelector('.announcement-bar-section')) {
              this.setAnnouncementHeight();
            }
            this.ticking = false;
          });
          this.ticking = true;
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });

      // Trigger scroll calculations once on load
      window.dispatchEvent(new Event('scroll'));

      // Trigger announcement bar resize if exists
      if (document.querySelector('.announcement-bar-section')) {
        window.dispatchEvent(new Event('resize'));
      }

      // Menu buttons
      if (this.menu) this.menu.querySelectorAll('summary').forEach(summary =>
        summary.addEventListener('click', this.onSummaryClick.bind(this))
      );
      if (this.menu) this.menu.querySelectorAll('.parent-link-back--button').forEach(button =>
        button.addEventListener('click', this.onCloseButtonClick.bind(this))
      );
    }

    // ------------------------------
    // Header Methods
    // ------------------------------

    // Inject CSS for native sticky positioning. Only applies when the
    // theme's header-sticky--active class is present (sticky_header setting
    // is enabled in the Shopify theme editor).
    injectStickyStyles() {
      if (document.getElementById('theme-header-native-sticky')) return;
      if (!this.classList.contains('header-sticky--active')) return;

      const styleEl = document.createElement('style');
      styleEl.id = 'theme-header-native-sticky';
      styleEl.textContent = `
        .header-section {
          position: -webkit-sticky;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        /* Cancel any leftover JS-applied transform on the inner element */
        theme-header.header-sticky--active {
          transform: none !important;
          transition: none;
        }
      `;
      document.head.appendChild(styleEl);
    }

    // Hysteresis prevents flicker at the boundary: once sticky, you have to
    // scroll back above 5px to remove the class; once not sticky, you have to
    // cross 15px to add it.
    setStickyClass() {
      const y = window.scrollY;
      if (this.classList.contains('is-sticky')) {
        if (y < 5) {
          this.classList.remove('is-sticky');
        }
      } else {
        if (y > 15) {
          this.classList.add('is-sticky');
        }
      }
    }

    setAnnouncementHeight() {
      const a_bar = document.getElementById('shopify-section-announcement-bar');
      if (a_bar) {
        let h = a_bar.clientHeight;
        document.documentElement.style.setProperty('--announcement-height', h + 'px');
      }
    }

    setHeaderOffset() {
      let h = this.header_section ? this.header_section.getBoundingClientRect().top : 0;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }

    setHeaderHeight() {
      let h = this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }

    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }

    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }

    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }

    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }

  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
      if (!this.submenus.length) return;

      const _this = this;

      // Resize on initial load with debounce
      window.addEventListener('resize', debounce(function () {
        _this.resizeSubMenus();
      }, 100));

      window.dispatchEvent(new Event('resize'));

      document.fonts.ready.then(function () {
        _this.resizeSubMenus();
      });
    }

    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.parentElement.clientWidth + 10,
            total = w + l;

          if (total > document.body.clientWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }

  customElements.define('full-menu', FullMenu);
}